/**
 * WEBSOCKET / SIGNALR ADMIN SERVICE: TUG OF WAR (ESTIRA Y AFLOJA)
 * Wrapper de comunicación en tiempo real para el Panel del Operador / Administrador del Estadio.
 * Gestiona la conexión persistente con SignalR Hub para recibir telemetría autoritativa a 100ms,
 * cambios de fase (cover, game_ready, countdown, playing, sudden_death, finished),
 * eventos de toques por bando, tracción de soga y proclamación del equipo vencedor.
 * 
 * @module utilities/WS/Admin/TugOfWarWS
 */

import SignalRService from '../SignalRService';

/**
 * Referencia al handler de escucha registrado en SignalRService para mensajes genéricos.
 * @type {Function|null}
 */
let registeredMessageHandler = null;

/**
 * Referencia al handler de reconexión exitosa registrado en SignalRService.
 * @type {Function|null}
 */
let registeredReconnectedHandler = null;

/**
 * Referencia al handler de reconexión en progreso registrado en SignalRService.
 * @type {Function|null}
 */
let registeredReconnectingHandler = null;

/**
 * Referencia al handler de cierre definitivo de conexión.
 * @type {Function|null}
 */
let registeredCloseHandler = null;

/**
 * Conecta el panel de administración al Hub de SignalR con el rol de operador de Estira y Afloja ('tugofwar:admin').
 * 
 * Eventos clave recibidos:
 * - 'tugofwar_cover_shown': Portada proyectada en la sala.
 * - 'tugofwar_game_scene_shown': Escenario de juego preparado.
 * - 'tugofwar_countdown_tick': Tick sincronizado de la cuenta regresiva (3, 2, 1).
 * - 'tugofwar_match_started': Inicio formal de la partida y activación de telemetría a 100ms.
 * - 'tugofwar_telemetry': Telemetría en tiempo real (RopeDisplacementVw, Tier, Scores, Fans, Taps).
 * - 'tugofwar_sudden_death_started': Activación de muerte súbita / tiempo extra por empate.
 * - 'tugofwar_game_over': Proclamación de ganador y estadísticas consolidadas.
 * - 'tugofwar_match_paused': Notificación de pausa de la partida.
 * - 'tugofwar_match_resumed': Notificación de reanudación de la partida.
 * - 'tugofwar_match_reset': Notificación de reinicio de la partida.
 * - 'tugofwar_fan_registered': Aficionado sumado a un bando.
 * - 'tugofwar_fan_unregistered': Aficionado desvinculado.
 * 
 * @param {Function} [onIncomingMessage=() => {}] - Callback para procesar los mensajes y eventos de telemetría emitidos por el Hub.
 * @param {Function} [onStatusChange=() => {}] - Callback para notificar cambios de estado ('connecting', 'connected', 'reconnecting', 'error', 'closed').
 * @param {string} [roomId=''] - Identificador GUID de la sala para filtrar el tráfico de eventos.
 */
export const ConnectWebSocketForTugOfWarAdmin = (
    onIncomingMessage = () => {},
    onStatusChange = () => {},
    roomId = ''
) => {
    onStatusChange('connecting');

    // Desuscribir handlers previos si existieran para evitar fugas de memoria o ejecuciones duplicadas
    if (registeredMessageHandler) {
        SignalRService.off('message', registeredMessageHandler);
    }
    if (registeredReconnectedHandler) {
        SignalRService.off('reconnected', registeredReconnectedHandler);
    }
    if (registeredReconnectingHandler) {
        SignalRService.off('reconnecting', registeredReconnectingHandler);
    }
    if (registeredCloseHandler) {
        SignalRService.off('close', registeredCloseHandler);
    }

    // Handler de mensajes entrantes con filtrado estricto por sala si fue provista
    registeredMessageHandler = (messageEnvelope) => {
        if (!messageEnvelope) return;

        const envelopeRoomId = messageEnvelope.roomId
            || messageEnvelope.RoomId
            || messageEnvelope.payload?.roomId
            || messageEnvelope.payload?.RoomId
            || messageEnvelope.data?.roomId
            || messageEnvelope.data?.RoomId;

        if (roomId && envelopeRoomId && String(envelopeRoomId).toLowerCase() !== String(roomId).toLowerCase()) {
            return;
        }

        if (typeof onIncomingMessage === 'function') {
            onIncomingMessage(messageEnvelope);
        }
    };

    registeredReconnectedHandler = () => {
        onStatusChange('connected');
    };

    registeredReconnectingHandler = () => {
        onStatusChange('reconnecting');
    };

    registeredCloseHandler = () => {
        onStatusChange('closed');
    };

    SignalRService.on('message', registeredMessageHandler);
    SignalRService.on('reconnected', registeredReconnectedHandler);
    SignalRService.on('reconnecting', registeredReconnectingHandler);
    SignalRService.on('close', registeredCloseHandler);

    SignalRService.connect({
        tipo: 'tugofwar:admin',
        roomId: roomId || ''
    })
        .then(() => {
            onStatusChange('connected');
        })
        .catch((connectionError) => {
            console.error('[TugOfWarAdminWS] Error al establecer conexión con el Hub de SignalR:', connectionError);
            onStatusChange('error');
        });
};

/**
 * Desconecta limpiamente la sesión SignalR del operador de Estira y Afloja y remueve los escuchas registrados.
 */
export const DisconnectWebSocketForTugOfWarAdmin = () => {
    try {
        if (registeredMessageHandler) {
            SignalRService.off('message', registeredMessageHandler);
            registeredMessageHandler = null;
        }
        if (registeredReconnectedHandler) {
            SignalRService.off('reconnected', registeredReconnectedHandler);
            registeredReconnectedHandler = null;
        }
        if (registeredReconnectingHandler) {
            SignalRService.off('reconnecting', registeredReconnectingHandler);
            registeredReconnectingHandler = null;
        }
        if (registeredCloseHandler) {
            SignalRService.off('close', registeredCloseHandler);
            registeredCloseHandler = null;
        }
        SignalRService.disconnect();
    } catch (disconnectionError) {
        console.error('[TugOfWarAdminWS] Error al desconectar la sesión de SignalR del operador:', disconnectionError);
    }
};

export default {
    ConnectWebSocketForTugOfWarAdmin,
    DisconnectWebSocketForTugOfWarAdmin
};
